import * as React from 'react';
import { useEffect, useRef } from 'react';
import TrashCan from '@uber/base-icons/trash-can';
import { type Theme, useStyletron } from 'baseui';
import { KEYPOINTS_PANEL } from '../../../constants/template-builder';
import type { KeypointItemT } from '../../../types/annotations.type';
import { ColorPickerPopover } from './color-picker-popover';
import { ColorSwatchButton } from './color-swatch-button';
type KeypointRowPropsT = {
  keypoint: KeypointItemT;
  isSelected?: boolean;
  onDelete?: (id: string) => void;
  onColorChange?: (id: string, color: string) => void;
};
const getRowStyles = (theme: Theme, isSelected: boolean) => ({
  display: ('flex' as const),
  alignItems: ('center' as const),
  alignSelf: ('stretch' as const),
  height: theme.sizing.scale1400,
  paddingRight: theme.sizing.scale300,
  backgroundColor: isSelected ? theme.colors.backgroundSecondary : 'transparent'
});
const getLabelStyles = (theme: Theme) => ({
  ...theme.typography.LabelMedium,
  flexGrow: 1,
  overflow: ('hidden' as const),
  textOverflow: ('ellipsis' as const),
  whiteSpace: ('nowrap' as const)
});
const getDeleteButtonStyles = (theme: Theme) => ({
  display: ('flex' as const),
  alignItems: ('center' as const),
  justifyContent: ('center' as const),
  width: theme.sizing.scale1200,
  height: theme.sizing.scale1200,
  background: 'none',
  border: 'none',
  cursor: ('pointer' as const),
  padding: '0',
  flexShrink: 0,
  color: theme.colors.contentSecondary
});
export function KeypointRow({
  keypoint,
  isSelected = false,
  onDelete,
  onColorChange
}: KeypointRowPropsT) {
  const [css, theme] = useStyletron();
  const rowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isSelected && rowRef.current) {
      rowRef.current.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [isSelected]);
  return <div ref={rowRef} className={css(getRowStyles(theme, isSelected))} data-testid={`keypoint-row-${keypoint.id}`}>
      <ColorPickerPopover selectedColor={keypoint.color} onColorChange={color => onColorChange?.(keypoint.id, color)}>
        <ColorSwatchButton color={keypoint.color} />
      </ColorPickerPopover>
      <div className={css(getLabelStyles(theme))}>{keypoint.label}</div>
      {onDelete && <button className={css(getDeleteButtonStyles(theme))} onClick={() => onDelete(keypoint.id)} aria-label={KEYPOINTS_PANEL.DELETE_LABEL(keypoint.label)} data-testid={`delete-keypoint-${keypoint.id}`}>
          <TrashCan size={16} />
        </button>}
    </div>;
}